import React, { useEffect, useRef, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Box,
  Button,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Send as SendIcon,
  History as HistoryIcon,
  PictureAsPdf as PdfIcon,
  Cancel as CancelIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import {
  formatRoleLabel,
  getStatusColor,
  getAircraftLabel,
  formatDateTime
} from './SalesEnquiryUtils';
import { canApproveAM, canApproveCAMO, canEditTrip, canCancelTrip } from './SalesEnquiry-part3';
import {
  charterTableContainerSx,
  charterHeaderCellSx,
  charterBodyRowSx,
  charterFirstCellSx,
  charterDataCellSx,
  charterPadRowSx
} from '../common/charterTableTheme';

const ROW_HEIGHT = 56;
const HEADER_HEIGHT = 40;

const formatFlightDate = (date) => {
  if (!date) return '—';
  return formatDateTime(date) || '—';
};

const TripsTable = ({
  trips,
  selectedTrip,
  onTripClick,
  onEditClick,
  onRemarksClick,
  onAuditTrailClick,
  onItineraryClick,
  onApprovalClick,
  onCancelClick,
  onReplicateClick,
  user,
  isAccountableManager,
  aircraft,
  canCreateTrips,
  onNewTripClick,
  fillHeight = false,
  expandControl = null
}) => {
  const tablePanelRef = useRef(null);
  const [padCount, setPadCount] = useState(0);

  useEffect(() => {
    if (!fillHeight) {
      setPadCount(Math.max(0, 6 - (trips?.length || 0)));
      return undefined;
    }
    const el = tablePanelRef.current;
    if (!el) return undefined;

    const TITLE_BLOCK = 56; // title row + mb: 2
    const updatePad = () => {
      const available = el.clientHeight - TITLE_BLOCK - HEADER_HEIGHT;
      const slots = Math.max(0, Math.floor(available / ROW_HEIGHT));
      setPadCount(Math.max(0, slots - (trips?.length || 0)));
    };

    updatePad();
    const observer = new ResizeObserver(() => {
      requestAnimationFrame(updatePad);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [fillHeight, trips?.length]);

  const escapeCsvValue = (value) => {
    if (value == null) return '';
    const stringValue = String(value);
    if (/[",\n\r]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const exportTripsToCsv = () => {
    if (!Array.isArray(trips) || trips.length === 0) return;

    const headers = [
      'Charter Number',
      'Trip Title',
      'Flight Date',
      'Flight Type',
      'Aircraft Assigned',
      'Status',
      'Pending Approvals',
      'Created By Name',
      'Created By Email',
      'Created At',
      'Updated At'
    ];

    const rows = trips.map((trip) => {
      const createdByName = trip.createdBy
        ? `${trip.createdBy.firstName || ''} ${trip.createdBy.lastName || ''}`.trim() || 'Unknown'
        : 'Unknown';
      const pendingApprovals = Array.isArray(trip.pendingApprovals)
        ? trip.pendingApprovals.map((approval) => formatRoleLabel(approval.role)).join('; ')
        : '';

      return [
        escapeCsvValue(trip.charterNumber || ''),
        escapeCsvValue(trip.title || ''),
        escapeCsvValue(formatFlightDate(trip.listFlightDate)),
        escapeCsvValue(trip.flightType || 'CHARTER'),
        escapeCsvValue(getAircraftLabel(trip.listAircraftId || trip.aircraftId, aircraft)),
        escapeCsvValue(trip.status || ''),
        escapeCsvValue(pendingApprovals),
        escapeCsvValue(createdByName),
        escapeCsvValue(trip.createdBy?.email || ''),
        escapeCsvValue(trip.createdAt ? new Date(trip.createdAt).toISOString() : ''),
        escapeCsvValue(trip.updatedAt ? new Date(trip.updatedAt).toISOString() : '')
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
    link.href = url;
    link.download = `sales-enquiry-trips-${timestamp}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box
      ref={tablePanelRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: fillHeight ? '100%' : 'auto',
        minHeight: 0
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="h6">Trips</Typography>
          {expandControl}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportTripsToCsv}
            disabled={!trips.length}
          >
            Export CSV
          </Button>
          {canCreateTrips && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onNewTripClick}
            >
              New Trip
            </Button>
          )}
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          ...charterTableContainerSx(fillHeight),
          height: fillHeight ? 0 : 376,
          maxHeight: fillHeight ? undefined : 376
        }}
      >
        <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={charterHeaderCellSx(true)}>Charter</TableCell>
              <TableCell sx={charterHeaderCellSx()}>Flight Date</TableCell>
              <TableCell sx={charterHeaderCellSx()}>Flight Type</TableCell>
              <TableCell sx={charterHeaderCellSx()}>Aircraft Assigned</TableCell>
              <TableCell sx={charterHeaderCellSx()}>Status</TableCell>
              <TableCell sx={charterHeaderCellSx()}>Created By</TableCell>
              <TableCell sx={charterHeaderCellSx()}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {trips.map((trip, index) => (
              <TableRow
                key={trip._id}
                hover
                onClick={() => onTripClick(trip._id)}
                sx={charterBodyRowSx(index, selectedTrip?._id === trip._id)}
              >
                <TableCell sx={charterFirstCellSx(index, selectedTrip?._id === trip._id)}>
                  {trip.charterNumber}
                </TableCell>
                <TableCell sx={charterDataCellSx(index)}>
                  {formatFlightDate(trip.listFlightDate)}
                </TableCell>
                <TableCell sx={charterDataCellSx(index)}>
                  <Chip
                    label={trip.flightType || 'CHARTER'}
                    size="small"
                    variant="outlined"
                    sx={{ height: '20px', fontSize: '0.7rem' }}
                  />
                </TableCell>
                <TableCell sx={charterDataCellSx(index)}>
                  {getAircraftLabel(trip.listAircraftId || trip.aircraftId, aircraft)}
                </TableCell>
                <TableCell sx={charterDataCellSx(index)}>
                  <Box>
                    <Chip
                      label={trip.status}
                      color={getStatusColor(trip.status)}
                      size="small"
                      sx={{ height: '20px', fontSize: '0.7rem' }}
                    />
                    {trip.pendingApprovals && trip.pendingApprovals.length > 0 && (
                      <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                        {trip.pendingApprovals.map((approval, idx) => (
                          <Chip
                            key={idx}
                            label={`⏳ ${formatRoleLabel(approval.role)}`}
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: '18px' }}
                          />
                        ))}
                      </Box>
                    )}
                    {trip.approvalStatus && trip.approvalStatus.allApproved && (
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label="✓ Approved"
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: '18px' }}
                        />
                      </Box>
                    )}
                    {canApproveAM(trip, user, isAccountableManager) && (
                      <Box sx={{ mt: 0.25 }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            onApprovalClick(trip, 'AM');
                          }}
                          sx={{ fontSize: '0.7rem', py: 0.25, minHeight: '24px' }}
                        >
                          Approve (AM)
                        </Button>
                      </Box>
                    )}
                    {canApproveCAMO(trip, user) && (
                      <Box sx={{ mt: 0.25 }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            onApprovalClick(trip, 'CA_MANAGER');
                          }}
                          sx={{ fontSize: '0.7rem', py: 0.25, minHeight: '24px' }}
                        >
                          Approve (CAMO)
                        </Button>
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={charterDataCellSx(index)}>
                  {trip.createdBy
                    ? `${trip.createdBy.firstName || ''} ${trip.createdBy.lastName || ''}`.trim() || trip.createdBy.email || 'Unknown'
                    : 'Unknown'}
                </TableCell>
                <TableCell sx={charterDataCellSx(index)}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAuditTrailClick(trip);
                    }}
                    title="View Audit Trail"
                  >
                    <HistoryIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onItineraryClick(trip);
                    }}
                    title="Generate/Download Itinerary"
                  >
                    <PdfIcon />
                  </IconButton>
                  {canCreateTrips && onReplicateClick && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onReplicateClick(trip);
                      }}
                      title="Replicate charter onto multiple days"
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  )}
                  {canEditTrip(trip, user) && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditClick(trip);
                      }}
                      title="Edit Trip"
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                  {trip.status === 'DRAFT' && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemarksClick(trip);
                      }}
                    >
                      <SendIcon />
                    </IconButton>
                  )}
                  {canCancelTrip(trip, user) && (
                    <Tooltip title="Cancel Charter">
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onCancelClick(trip);
                          }}
                          color="error"
                        >
                          <CancelIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {Array.from({ length: padCount }).map((_, padIndex) => {
              const index = (trips?.length || 0) + padIndex;
              return (
                <TableRow
                  key={`empty-row-${padIndex}`}
                  sx={charterPadRowSx(index)}
                >
                  <TableCell sx={charterFirstCellSx(index)}>&nbsp;</TableCell>
                  <TableCell sx={charterDataCellSx(index)} colSpan={6}>&nbsp;</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TripsTable;
